// Livewire request error reporter.
// Catches 5xx responses from Livewire's request lifecycle, suppresses the
// default dark error overlay, and surfaces a persistent role="alert" banner
// because a 5xx leaves the component state out of sync with the server —
// the page is functionally broken and the user must refresh to recover.
// A toast (aria-live="polite", auto-dismiss) is the wrong pattern for that
// failure mode; role="alert" announces immediately and the banner stays
// put until the user clicks Refresh.
//
// To avoid duplicate Flare events, the server tags handler-rendered 5xx
// responses with X-Flare-Reported: 1 (see App\Exceptions\Handler::render).
// When the marker is present, Laravel's report() already fired and this
// hook skips its own POST. When the marker is absent — e.g. PHP fatal that
// killed FPM before the handler ran — the hook POSTs to a server endpoint
// so the report originates from a healthy PHP process.

import { impersonationTimeLimitRedirect } from './impersonation-time-limit.js';

const ENDPOINT = '/_livewire-error-report';
const SNAPSHOT_MAX = 8000;
const BANNER_ID = 'livewire-error-banner';

function reportToServer(payload) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    if (!csrf) return;

    fetch(ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrf,
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'same-origin',
    }).catch(() => { /* swallow; nothing we can do */ });
}

function showFailureBanner() {
    if (document.getElementById(BANNER_ID)) return; // already shown

    const banner = document.createElement('div');
    banner.id = BANNER_ID;
    banner.setAttribute('role', 'alert');
    banner.style.cssText = [
        'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:2147483647',
        'background-color:#b91c1c', 'color:#ffffff',
        'padding:12px 20px',
        'display:flex', 'gap:16px', 'justify-content:center', 'align-items:center', 'flex-wrap:wrap',
        'font-family:system-ui,-apple-system,BlinkMacSystemFont,sans-serif',
        'font-size:14px', 'line-height:1.4',
        'box-shadow:0 2px 8px rgba(0,0,0,0.2)',
    ].join(';');

    const message = document.createElement('span');
    message.innerHTML = '<strong>Something went wrong.</strong> Please refresh the page to recover.';
    banner.appendChild(message);

    const refresh = document.createElement('button');
    refresh.type = 'button';
    refresh.textContent = 'Refresh page';
    refresh.style.cssText = [
        'background-color:#ffffff', 'color:#b91c1c', 'border:0',
        'padding:8px 16px', 'border-radius:4px',
        'font-weight:600', 'cursor:pointer',
        'font-family:inherit', 'font-size:inherit',
    ].join(';');
    refresh.addEventListener('click', () => window.location.reload());
    banner.appendChild(refresh);

    document.body.prepend(banner);
    refresh.focus();
}

document.addEventListener('livewire:init', () => {
    if (!window.Livewire) return;

    window.Livewire.hook('request', ({ url, respond, fail }) => {
        // Server-rendered 5xx responses are tagged with X-Flare-Reported by the
        // Laravel handler — capture that signal before fail() runs.
        let alreadyReported = false;
        respond(({ response }) => {
            try {
                if (response?.headers?.get('X-Flare-Reported') === '1') {
                    alreadyReported = true;
                }
            } catch (_) { /* response unavailable, treat as unreported */ }
        });

        fail(({ status, content, preventDefault }) => {
            // Every failure — including the synthesized 503 for a dropped
            // connection — must reach optimistic UI (the instance card's
            // fly-away) so it can undo itself: $wire.call's promise never
            // rejects on a failed request, so this event is the only signal.
            window.dispatchEvent(new CustomEvent('aaa-livewire-request-failed'));

            // A Nova impersonation past its time limit was just ended on the server
            // (EndExpiredNovaImpersonation). Go where it says instead of showing the 403.
            const timeLimitRedirect = impersonationTimeLimitRedirect(status, content, window.location.origin);
            if (timeLimitRedirect) {
                preventDefault();
                window.location.assign(timeLimitRedirect);
                return;
            }

            if (status < 500) return;
            if (typeof url === 'string' && url.includes('_livewire-error-report')) return;

            // Livewire synthesizes { status: 503, content: null } when fetch()
            // itself throws — i.e. the in-flight request was aborted by a page
            // refresh/navigation, or the network briefly dropped. No response
            // ever came back from the server, so this is a client-side event,
            // not a server error worth paging on. See vendor livewire.esm.js
            // sendRequest() catch block.
            if (content === null) return;

            preventDefault();
            showFailureBanner();

            if (alreadyReported) return;

            reportToServer({
                status,
                url: typeof url === 'string' ? url : String(url ?? ''),
                snapshot: typeof content === 'string' ? content.substring(0, SNAPSHOT_MAX) : null,
            });
        });
    });
});
